import { CarIcon, TruckIcon, BusIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type VehicleCategory = 'SUV' | 'Sedan'
export type VehicleType = 'Car' | 'Jeep' | 'Hiace' | 'Bus'

export interface Vehicle {
  id: string
  name: string
  category: VehicleCategory
  type: VehicleType
  seats: number
  luggage: number
  transmission: 'Automatic' | 'Manual'
  image: string
  dayRate: number
  nightRate: number
  features: string[]
  driverExperience: string
  includedKm: string
  inclusions: string[]
  popular?: boolean
}

// Rates are per day in NPR. Night rates carry a surcharge for after-hours driving.
export const fleet: Vehicle[] = [
  {
    id: 'sedan-comfort',
    name: 'City Sedan',
    category: 'Sedan',
    type: 'Car',
    seats: 4,
    luggage: 2,
    transmission: 'Automatic',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a760c497-1a77-4be9-ba69-9aeec881b4bc.jpg',
    dayRate: 3500,
    nightRate: 4200,
    features: ['Air conditioning', 'Fuel efficient', 'City friendly'],
    driverExperience: '8+ years experienced',
    includedKm: 'Up to 250 km included',
    inclusions: ['Fuel included', 'Parking included', 'Toll charges included', 'Complimentary water'],
    popular: true,
  },
  {
    id: 'suv-explorer',
    name: 'Explorer SUV',
    category: 'SUV',
    type: 'Car',
    seats: 5,
    luggage: 4,
    transmission: 'Automatic',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/388b4ccd-e1c8-4d31-8085-52feed54c722.jpg',
    dayRate: 6500,
    nightRate: 7800,
    features: ['Spacious cabin', 'Highway ready', 'Extra luggage'],
    driverExperience: '10+ years experienced',
    includedKm: 'Up to 300 km included',
    inclusions: ['Fuel included', 'Parking included', 'Toll charges included', 'Complimentary water'],
    popular: true,
  },
  {
    id: 'jeep-offroad',
    name: 'Trail Jeep 4x4',
    category: 'SUV',
    type: 'Jeep',
    seats: 4,
    luggage: 3,
    transmission: 'Manual',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e71bbcee-282b-4724-ab89-d7a2a55767e1.jpg',
    dayRate: 8500,
    nightRate: 10200,
    features: ['Off-road capable', 'Hill climbs', 'Rugged build'],
    driverExperience: '12+ years mountain routes',
    includedKm: 'Up to 280 km included',
    inclusions: ['Fuel included', 'Parking included', 'Toll charges included', 'First-aid kit'],
  },
  {
    id: 'hiace-tourer',
    name: 'Tourer Hiace',
    category: 'SUV',
    type: 'Hiace',
    seats: 12,
    luggage: 8,
    transmission: 'Manual',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3e110de4-2217-41c6-bf3d-ffd5910b4b04.jpg',
    dayRate: 9500,
    nightRate: 11400,
    features: ['Group travel', 'Large luggage bay', 'Comfy seating'],
    driverExperience: '10+ years group travel',
    includedKm: 'Up to 300 km included',
    inclusions: ['Fuel included', 'Parking included', 'Toll charges included', 'Complimentary water'],
  },
]

export const vehicleTypeMeta: Record<VehicleType, { label: string; icon: LucideIcon }> = {
  Car: { label: 'Car', icon: CarIcon },
  Jeep: { label: 'Jeep', icon: TruckIcon },
  Hiace: { label: 'Hiace', icon: BusIcon },
  Bus: { label: 'Bus', icon: BusIcon },
}

export const formatNpr = (value: number): string =>
  'Rs. ' + value.toLocaleString('en-IN')
